import { DataTableFilterDropdown, type DataTableFilterDropdownChromeProps } from './FilterDropdown';
import { isExpandAction } from './actions';
import { dataTableButtonClasses } from './buttonStyles';
import { DataTableExpandToggle } from './Toolbar';
import type { DataTableAction, DataTableFilter } from './types';
import { cx } from './utils';

export interface DataTableFiltersProps {
    filters: readonly DataTableFilter[];
    className?: string;
}

/**
 * The presentation-only half of a filter, common to all three variants. Split
 * out so the branches below differ only in what discriminates them, and so
 * `key` - which React warns about if it reaches the props object - stays out.
 */
function chromeProps(filter: DataTableFilter): DataTableFilterDropdownChromeProps {
    return {
        label: filter.label,
        align: filter.align,
        searchable: filter.searchable,
        disabled: filter.disabled,
        className: filter.className,
        buttonClassName: filter.buttonClassName,
        footer: filter.footer,
        workQueueVariant: filter.workQueueVariant,
        forceActive: filter.forceActive
    };
}

/**
 * Renders the declarative `filters` array as filter pills.
 *
 * The selection half is passed explicitly per branch rather than spread, so the
 * sectioned/single/multi discrimination survives the way in.
 */
export function DataTableFilters({ filters, className }: DataTableFiltersProps) {
    return (
        <div className={cx('flex items-center gap-2.5', className)}>
            {filters.map((filter) => {
                if (filter.sections) {
                    return (
                        <DataTableFilterDropdown
                            key={filter.key}
                            {...chromeProps(filter)}
                            sections={filter.sections}
                        />
                    );
                }
                return filter.multiple ? (
                    <DataTableFilterDropdown
                        key={filter.key}
                        {...chromeProps(filter)}
                        multiple
                        options={filter.options}
                        value={filter.value}
                        onChange={filter.onChange}
                    />
                ) : (
                    <DataTableFilterDropdown
                        key={filter.key}
                        {...chromeProps(filter)}
                        options={filter.options}
                        value={filter.value}
                        onChange={filter.onChange}
                    />
                );
            })}
        </div>
    );
}

export interface DataTableActionsProps {
    actions: readonly DataTableAction[];
    className?: string;
}

/**
 * Renders the declarative `actions` array as design-system toolbar buttons.
 * An `expandAction` entry renders as the standard expand toggle, positioned
 * exactly like its button siblings - array order is render order.
 */
export function DataTableActions({ actions, className }: DataTableActionsProps) {
    return (
        <div className={cx('flex items-center gap-2.5', className)}>
            {actions.map((action) =>
                isExpandAction(action) ? (
                    <DataTableExpandToggle
                        key={action.key ?? 'expand'}
                        expansion={action.expansion}
                        label={action.label}
                        className={action.className}
                    />
                ) : (
                    <button
                        key={action.key ?? action.label}
                        type="button"
                        onClick={action.onClick}
                        disabled={action.disabled}
                        title={action.title}
                        className={dataTableButtonClasses(action.variant, 'md', action.className)}
                    >
                        {action.icon}
                        {action.label}
                    </button>
                )
            )}
        </div>
    );
}
